import React, { useState, useEffect } from "react";
import { Card, Button, Table, Form, Modal, Alert, Spinner } from "react-bootstrap";
import "../styles/UserProfiles.css"; // custom CSS overrides
import authService from "../services/authService"; // Import your auth service

function UserProfile() {
  const [activeTab, setActiveTab] = useState("account");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // User data state
  const [userData, setUserData] = useState({
    full_name: "",
    email: "",
    customer_code: "",
    type: "",
    loyalty_points: "",
    total_spent: "",
    total_orders: 0,
    notes: "",
    created_at: ""
  });

  // Form data state
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    mobile_number: ""
  });

  // Address state
  const [addresses, setAddresses] = useState([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [newAddress, setNewAddress] = useState({ label: "", address: "" });

  // Orders state
  const [orders, setOrders] = useState([]);
  const [orderDetails, setOrderDetails] = useState({});

  // Load user data on component mount
  useEffect(() => {
    loadUserData();
    if (activeTab === "orders") {
      loadOrders();
    }
  }, [activeTab]);

  // Load user data from API using authService
  const loadUserData = async () => {
    try {
      setLoading(true);
      const data = await authService.getProfile();
      
      if (data.success) {
        setUserData(data.data.customer);
        setFormData({
          full_name: data.data.customer.full_name,
          email: data.data.customer.email,
          mobile_number: "" // You might need to get this from another endpoint
        });
      }
    } catch (error) {
      console.error("Error loading user data:", error);
      setError("Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  // Load orders from API using authService
  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await authService.getOrders();
      
      if (data.success) {
        setOrders(data.data.orders || []);
      }
    } catch (error) {
      console.error("Error loading orders:", error);
      setError("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  // Load specific order details using authService
  const loadOrderDetails = async (orderId) => {
    try {
      const data = await authService.getOrderDetails(orderId);
      
      if (data.success) {
        setOrderDetails(prev => ({
          ...prev,
          [orderId]: data.data.order
        }));
      }
    } catch (error) {
      console.error("Error loading order details:", error);
      setError("Failed to load order details");
    }
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      setLoading(true);
      const data = await authService.updateProfile(formData);
      
      if (data.success) {
        setSuccess("Profile updated successfully");
        setUserData(data.data.customer);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setError("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddress = () => {
    if (newAddress.label && newAddress.address) {
      setAddresses([...addresses, { id: Date.now(), ...newAddress }]);
      setNewAddress({ label: "", address: "" });
      setShowAddressModal(false);
    }
  };

  const handleDeleteAddress = (id) => {
    setAddresses(addresses.filter((addr) => addr.id !== id));
  };

  const toggleOrderDetails = async (orderId) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null);
    } else {
      setExpandedOrder(orderId);
      if (!orderDetails[orderId]) {
        await loadOrderDetails(orderId);
      }
    }
  };

  const renderOrderItems = (order) => {
    const details = orderDetails[order.id];
    
    if (!details) {
      return <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>;
    }

    return (
      <div className="p-3">
        <div className="row mb-3">
          <div className="col-md-3">
            <strong>Date:</strong> {new Date(order.created_at).toLocaleDateString()}
          </div>
          <div className="col-md-3">
            <strong>Total:</strong> Rs.{parseFloat(order.total_amount).toFixed(2)}
          </div>
          <div className="col-md-3">
            <strong>Status:</strong> 
            <span className={`badge ${getStatusBadgeClass(order.status)} ms-1`}>
              {order.status}
            </span>
          </div>
          <div className="col-md-3">
            <strong>Payment:</strong> {order.payment_method || "N/A"}
          </div>
        </div>
        
        <h6 className="mb-2">Order Items:</h6>
        <Table size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Product</th>
              <th>Quantity</th>
              <th>Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {(details.items || []).map((item, index) => (
              <tr key={index}>
                <td>{item.product_name}</td>
                <td>{item.quantity}</td>
                <td>Rs.{parseFloat(item.unit_price).toFixed(2)}</td>
                <td>Rs.{parseFloat(item.total_price).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-success';
      case 'pending':
        return 'bg-warning';
      case 'cancelled':
        return 'bg-danger';
      case 'processing':
        return 'bg-info';
      default:
        return 'bg-secondary';
    }
  };

  const renderContent = () => {
    if (loading && activeTab !== "orders") {
      return (
        <Card className="p-4 shadow-sm border-card text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2 mb-0">Loading...</p>
        </Card>
      );
    }

    switch (activeTab) {
      case "account":
        return (
          <Card className="p-4 shadow-sm border-card">
            <h4>Account Details</h4>
            
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}
            
            <Form onSubmit={handleUpdateProfile}>
              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <Form.Control 
                  type="text" 
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleFormChange}
                  placeholder="Enter full name" 
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  placeholder="Enter email" 
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Mobile Number</Form.Label>
                <Form.Control 
                  type="tel" 
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleFormChange}
                  placeholder="Enter mobile number" 
                />
              </Form.Group>
              
              {/* Display additional user info */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Customer Code:</strong> {userData.customer_code}
                </div>
                <div className="col-md-6">
                  <strong>Loyalty Points:</strong> {userData.loyalty_points}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Total Orders:</strong> {userData.total_orders}
                </div>
                <div className="col-md-6">
                  <strong>Total Spent:</strong> Rs.{parseFloat(userData.total_spent || 0).toFixed(2)}
                </div>
              </div>
              
              <Button 
                className="primary-btn" 
                size="sm" 
                type="submit"
                disabled={true} // Disabled as per requirement
              >
                Update (Disabled)
              </Button>
              <div className="text-muted small mt-1">
                Profile updates are currently disabled
              </div>
            </Form>
          </Card>
        );

      case "addresses":
        return (
          <Card className="p-4 shadow-sm border-card">
            <h4>Saved Addresses</h4>
            <div className="row">
              {addresses.map((addr) => (
                <div className="col-md-6 mb-3" key={addr.id}>
                  <Card className="p-3 position-relative shadow-sm border-card">
                    <button
                      className="btn btn-sm btn-danger position-absolute"
                      style={{ top: "10px", right: "10px" }}
                      onClick={() => handleDeleteAddress(addr.id)}
                    >
                      ✕
                    </button>
                    <h6 className="fw-bold">{addr.label}</h6>
                    <p className="mb-0 text-muted">{addr.address}</p>
                  </Card>
                </div>
              ))}
            </div>
            <Button 
              className="primary-btn" 
              size="sm" 
              onClick={() => setShowAddressModal(true)}
              disabled={true} // Disabled as per requirement
            >
              + Add New Address (Disabled)
            </Button>
            <div className="text-muted small mt-1">
              Address management is currently disabled
            </div>

            {/* Address Modal */}
            <Modal show={showAddressModal} onHide={() => setShowAddressModal(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>Add New Address</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Label</Form.Label>
                    <Form.Control
                      type="text"
                      value={newAddress.label}
                      onChange={(e) =>
                        setNewAddress({ ...newAddress, label: e.target.value })
                      }
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      type="text"
                      value={newAddress.address}
                      onChange={(e) =>
                        setNewAddress({ ...newAddress, address: e.target.value })
                      }
                    />
                  </Form.Group>
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowAddressModal(false)}
                >
                  Cancel
                </Button>
                <Button className="primary-btn" size="sm" onClick={handleAddAddress}>
                  Save Address
                </Button>
              </Modal.Footer>
            </Modal>
          </Card>
        );

      case "payment":
        return (
          <Card className="p-4 shadow-sm border-card">
            <h4>Payment Methods</h4>
            <div className="d-flex gap-3 flex-wrap">
              {/* Payment methods can be added here when enabled */}
            </div>
            <Button 
              className="primary-btn mt-3" 
              size="sm"
              disabled={true} // Disabled as per requirement
            >
              + Add Payment Method (Disabled)
            </Button>
            <div className="text-muted small mt-1">
              Payment method management is currently disabled
            </div>
          </Card>
        );

      case "orders":
        return (
          <Card className="p-4 shadow-sm border-card">
            <h4 className="mb-3">Order History</h4>
            
            {error && <Alert variant="danger">{error}</Alert>}
            
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading orders...</span>
                </Spinner>
                <p className="mt-2 mb-0">Loading orders...</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">No orders found</p>
              </div>
            ) : (
              <Table responsive hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Order ID</th>
                    <th>Date</th>
                    <th>Total Amount</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <React.Fragment key={order.id}>
                      <tr>
                        <td>
                          <span className="badge bg-info text-dark">{order.order_number}</span>
                        </td>
                        <td>{new Date(order.created_at).toLocaleDateString()}</td>
                        <td>Rs.{parseFloat(order.total_amount).toFixed(2)}</td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                        <td>
                          <Button
                            className="custom-btn"
                            size="sm"
                            onClick={() => toggleOrderDetails(order.id)}
                          >
                            {expandedOrder === order.id ? "▲" : "▼"}
                          </Button>
                        </td>
                      </tr>
                      {expandedOrder === order.id && (
                        <tr>
                          <td colSpan="5" className="bg-light p-0">
                            {renderOrderItems(order)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container mt-5" style={{ maxWidth: "900px" }}>
      {/* Tabs */}
      <ul className="nav nav-tabs mb-4 custom-tabs">
        <li className="nav-item">
          <button
            className={activeTab === "account" ? "active-tab" : "inactive-tab"}
            onClick={() => setActiveTab("account")}
          >
            Account Details
          </button>
        </li>
        <li className="nav-item">
          <button
            className={activeTab === "addresses" ? "active-tab" : "inactive-tab"}
            onClick={() => setActiveTab("addresses")}
          >
            Saved Addresses
          </button>
        </li>
        <li className="nav-item">
          <button
            className={activeTab === "payment" ? "active-tab" : "inactive-tab"}
            onClick={() => setActiveTab("payment")}
          >
            Payment Methods
          </button>
        </li>
        <li className="nav-item">
          <button
            className={activeTab === "orders" ? "active-tab" : "inactive-tab"}
            onClick={() => setActiveTab("orders")}
          >
            Order History
          </button>
        </li>
      </ul>

      {/* Content */}
      {renderContent()}
    </div>
  );
}

export default UserProfile;